/**
 * Couleurs « Ma marque » (formule Pro) → jetons CSS de la console.
 *
 * Les écrans TV et téléphone utilisent `--pink` à la fois comme FOND de bouton
 * (texte blanc dessus) et comme COULEUR DE TEXTE sur le fond nuit (`--bg`
 * #070219) ; `--gold` sert surtout de texte / pastille sur ce même fond. Une
 * couleur de marque prise telle quelle (jaune vif, bleu marine…) rendrait
 * l'un des deux illisible : on garde la teinte et on ne bouge que la
 * luminosité, jusqu'au contraste minimum. L'aperçu de Mon compte montre la
 * couleur corrigée : ce qu'on voit est ce que la TV affichera.
 */

export const DEFAULT_PRIMARY = '#ff2453';
export const DEFAULT_SECONDARY = '#f3ca20';

const HEX = /^#[0-9a-f]{6}$/i;

/** Fond nuit des écrans (styles/redesign-tokens.css `--bg`). */
const NIGHT_BG = '#070219';
/** Contraste minimum (WCAG « grand texte » / composants) : boutons et titres de la TV. */
const MIN_CONTRAST = 3;

export function isHexColor(value: unknown): value is string {
    return typeof value === 'string' && HEX.test(value);
}

function toRgb(hex: string): [number, number, number] {
    return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

function toHex([r, g, b]: [number, number, number]): string {
    return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}

function luminance(hex: string): number {
    const [r, g, b] = toRgb(hex).map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: string, b: string): number {
    const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}

function toHsl(hex: string): [number, number, number] {
    const [r, g, b] = toRgb(hex).map((c) => c / 255);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    return [h / 6, s, l];
}

function fromHsl([h, s, l]: [number, number, number]): string {
    if (s === 0) return toHex([l * 255, l * 255, l * 255]);
    const hue = (p: number, q: number, t: number) => {
        let u = t;
        if (u < 0) u += 1;
        if (u > 1) u -= 1;
        if (u < 1 / 6) return p + (q - p) * 6 * u;
        if (u < 1 / 2) return q;
        if (u < 2 / 3) return p + (q - p) * (2 / 3 - u) * 6;
        return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return toHex([hue(p, q, h + 1 / 3) * 255, hue(p, q, h) * 255, hue(p, q, h - 1 / 3) * 255]);
}

/**
 * Couleur de même teinte la plus proche (en luminosité) qui passe `ok`.
 * Balayage par pas de 1 % de part et d'autre : 100 essais au pire, sans surprise.
 */
function nearestLightness(hex: string, ok: (c: string) => boolean): string {
    if (ok(hex)) return hex;
    const [h, s, l] = toHsl(hex);
    for (let step = 0.01; step <= 1; step += 0.01) {
        const found = [l - step, l + step]
            .filter((candidate) => candidate >= 0 && candidate <= 1)
            .map((candidate) => fromHsl([h, s, candidate]))
            .find(ok);
        if (found) return found;
    }
    return hex;
}

/** Couleur principale : lisible en fond de bouton (texte blanc) ET en texte sur le fond nuit. */
export function safePrimary(hex: string): string {
    if (!isHexColor(hex)) return DEFAULT_PRIMARY;
    return nearestLightness(
        hex.toLowerCase(),
        (c) => contrast(c, '#ffffff') >= MIN_CONTRAST && contrast(c, NIGHT_BG) >= MIN_CONTRAST
    );
}

/** Couleur secondaire : lisible en texte / pastille sur le fond nuit. */
export function safeSecondary(hex: string): string {
    if (!isHexColor(hex)) return DEFAULT_SECONDARY;
    return nearestLightness(hex.toLowerCase(), (c) => contrast(c, NIGHT_BG) >= 4.5);
}

function withAlpha(hex: string, alpha: number): string {
    const [r, g, b] = toRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Jetons CSS à poser sur la racine d'un écran de marque (ou sur l'aperçu).
 * Nouveaux écrans : `--pink` / `--gold` (styles/redesign-tokens.css).
 * Anciens écrans (cadres des jeux, téléphone) : `--color-primary*` (index.css,
 * même jeu de variables que la charte Orly `public/assets/css/airport-ORY.css`).
 */
export function brandTokens(primary: string, secondary: string): Record<string, string> {
    const p = safePrimary(primary);
    const s = safeSecondary(secondary);
    const alphas: Array<[string, number]> = [
        ['0', 0],
        ['10', 0.1],
        ['25', 0.25],
        ['50', 0.5],
        ['75', 0.75],
        ['100', 1],
    ];
    return {
        '--pink': p,
        '--pink-soft': withAlpha(p, 0.14),
        '--gold': s,
        '--gold-soft': withAlpha(s, 0.14),
        '--color-primary': p,
        '--color-primary-c500': p,
        ...Object.fromEntries(alphas.map(([k, a]) => [`--color-primary-${k}`, withAlpha(p, a)])),
    };
}
